import React, { useEffect, useRef, useState } from 'react';
import { useScreenBreakpoint } from 'consts/breakpoints';
import * as Styled from './styled';
import ArrowLeft from 'assets/svg/arrow-left.svg';
import ArrowRight from 'assets/svg/arrow-right.svg';
import { getInfiniteNextIndex, getInfinitePrevIndex } from 'utils/gallery';
import { createPanResponder } from 'helpers/pan-responder';
import { ReviewCard } from 'screens/MortgageOfficePage/components/ReviewCard';
import { ForwardRefProps, InfiniteSlider, InfinityDirection } from 'screens/MortgageOfficePage/components/InfiniteSlider';
import { useLocale } from 'locale';
import { REVIEW_SIZE } from 'screens/MortgageOfficePage/components/ReviewCard/styled';
import { useWindowSize } from 'hooks/useWindowSize';
import { MortgageOfficeRecomendations } from 'utils/entities';
import withEvents from 'analytics/withEvents';
import { Action, Direction, EventProps, PageDetails } from 'screens/AgentOfficePages/types';
import { useRoute } from 'react-router5';
import { Route } from 'config/routes';
import { getAgentOfficeCategory } from 'screens/AgentOfficePages/helper';


interface ReviewsProps {
  list: MortgageOfficeRecomendations[];
  pageDetails?: PageDetails;
  onCarouselScroll?: (props: EventProps) => void;
}

const ReviewsBase: React.FC<ReviewsProps> = ({ list, onCarouselScroll, pageDetails }) => {
  const { t } = useLocale();
  const [ windowWidth ] = useWindowSize();
  const isMobile = useScreenBreakpoint() === 1;
  const isDesktop = useScreenBreakpoint() >= 3;
  const [ slideIndex, setSlideIndex ] = useState(0);
  const infinityCarouselRef = useRef<ForwardRefProps>(null);
  const [ isHovered, setIsHovered ] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  let interval: NodeJS.Timeout = null;
  const isStaticDesktopWidth = isDesktop && list.length < 4;
  const { route } = useRoute();
  const isOfficePage = route.name === Route.OfficePage;
  const event = isOfficePage ? 'office_carousel_scroll' : 'agent_carousel_scroll';
  const category = getAgentOfficeCategory(isOfficePage);
  const eventHandler = (direction: Direction, action: Action) => {
    onCarouselScroll({
      event,
      category,
      direction,
      action,
      pageDetails,
    });
  };

  const onPrevSlide = () => {
    const total = list.length;
    const nextSlideIndex = getInfinitePrevIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
    eventHandler(Direction.Right, Action.Swipe);
  };

  const onNextSlide = () => {
    const total = list.length;
    const nextSlideIndex = getInfiniteNextIndex(slideIndex, total);
    setSlideIndex(nextSlideIndex);
    eventHandler(Direction.Left, Action.Swipe);
  };

  const panHandlers = createPanResponder({
    onStart: () => setIsHovered(true),
    onMove: () => setIsHovered(true),
    onEnd: () => setIsHovered(false),
  });

  const handleChangeInfinityDirection = (d: InfinityDirection, skipOnScrollCb?: boolean) => {
    if (infinityCarouselRef.current) infinityCarouselRef.current.move(d, skipOnScrollCb);
  };

  const onScrollHandler = (d: Direction) => {
    eventHandler(d, isDesktop ? Action.Click : Action.Swipe);
  };

  useEffect(() => {
    if (isHovered) {
      clearInterval(interval);
    }
    else {
      interval = setInterval(() => handleChangeInfinityDirection(InfinityDirection.Prev, false), 5000);
    }
    return () => clearInterval(interval);
  }, [ isHovered, isMobile, handleChangeInfinityDirection ]);

  const galleryContent = (
    <InfiniteSlider
      elementHeight={REVIEW_SIZE.height[isMobile ? 'mobile' : 'desktop']}
      elementWidth={isMobile ? windowWidth - 48 : REVIEW_SIZE.width}
      disableSwipe={isDesktop}
      spacing={isDesktop ? 18 : 8}
      ref={infinityCarouselRef}
      position="center"
      onChangeEvent={onScrollHandler}
    >
      {list.map((review, i) => (
        <Styled.InfinityItemWrapper key={i} style={{ width: isMobile ? windowWidth - 48 : '100%' }}>
          <ReviewCard {...review} />
        </Styled.InfinityItemWrapper>
      ))}
    </InfiniteSlider>
  );

  const staticContent = (
    <Styled.StaticReviewWrapper fullWidth={isDesktop && list.length === 3}>
      {list.map((review, i) => (
        <ReviewCard key={i} {...review} />
      ))}
    </Styled.StaticReviewWrapper>
  );

  return (
    <>
      <Styled.Wrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={sectionRef}
        {...panHandlers}
      >
        <Styled.TitleWrapper>
          <Styled.Title>{t('mortgage.officePage.reviews.title')}</Styled.Title>
          <Styled.Subtitle>{t('mortgage.officePage.reviews.subtitle', { count: list.length })}</Styled.Subtitle>
        </Styled.TitleWrapper>
        {list.length ? (
          <Styled.CarouselWrapper>
            {(!isDesktop && list.length === 1 || isStaticDesktopWidth) ? staticContent : galleryContent}
            {isDesktop && list.length > 2 && !isStaticDesktopWidth ? (
              <>
                <Styled.ArrowLeftWrapper
                  className="arrows"
                  onClick={isMobile ? onNextSlide : () => handleChangeInfinityDirection(InfinityDirection.Next)}
                >
                  <Styled.ArrowCircleLeft>
                    <ArrowLeft />
                  </Styled.ArrowCircleLeft>
                </Styled.ArrowLeftWrapper>
                <Styled.ArrowRightWrapper
                  className="arrows"
                  onClick={isMobile ? onPrevSlide : () => handleChangeInfinityDirection(InfinityDirection.Prev)}
                >
                  <Styled.ArrowCircleRight>
                    <ArrowRight />
                  </Styled.ArrowCircleRight>
                </Styled.ArrowRightWrapper>
              </>
            ) : null}
          </Styled.CarouselWrapper>
        ) : null}
      </Styled.Wrapper>
    </>
  );
};

export const Reviews = withEvents<ReviewsProps>(sendEvent => ({
  onCarouselScroll(props: EventProps) {
    sendEvent(props.event, props.category, {
      ...props.pageDetails,
      event: {
        carousel_type: 'reviews',
        direction: props.direction,
        action: props.action,
      },
    });
  },
}))(ReviewsBase);
